import React, { useState } from 'react'
import { useMutation, gql } from '@apollo/client'
import { withTranslation } from 'react-i18next'
import { createRider, editRider, getRiders, getAvailableRiders } from '../../apollo'
import {
  Box, Typography, TextField, Button, Alert, CircularProgress, Switch
} from '@mui/material'
import TwoWheelerIcon from '@mui/icons-material/TwoWheeler'

const CREATE_RIDER = gql`${createRider}`
const EDIT_RIDER = gql`${editRider}`
const GET_RIDERS = gql`${getRiders}`
const GET_AVAILABLE_RIDERS = gql`${getAvailableRiders}`

function Rider(props) {
  const isEdit = !!props.rider
  const mutation = isEdit ? EDIT_RIDER : CREATE_RIDER

  const [name, setName] = useState(props.rider?.name || '')
  const [email, setEmail] = useState(props.rider?.username || '')
  const [phone, setPhone] = useState(props.rider?.phone || '')
  const [available, setAvailable] = useState(props.rider ? props.rider.available : true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const [mutate, { loading }] = useMutation(mutation, {
    refetchQueries: [{ query: GET_RIDERS }, { query: GET_AVAILABLE_RIDERS }],
    onCompleted: () => {
      setSuccess(isEdit ? 'Rider updated successfully!' : 'Rider created! Temporary password sent to their email.')
      setError('')
      setTimeout(() => { if (props.onClose) props.onClose() }, 1800)
    },
    onError: err => {
      setError(err.graphQLErrors?.[0]?.message || err.message || 'Something went wrong')
      setSuccess('')
    }
  })

  const handleSubmit = () => {
    if (!name.trim()) { setError('Name is required'); return }
    if (!email.trim()) { setError('Email is required'); return }
    setError('')

    if (isEdit) {
      mutate({
        variables: {
          riderInput: {
            _id: props.rider._id,
            name: name.trim(),
            username: email.trim(),
            phone: phone.trim(),
            available
          }
        }
      })
    } else {
      mutate({
        variables: {
          riderInput: {
            name: name.trim(),
            email: email.trim(),
            phone: phone.trim(),
            available
          }
        }
      })
    }
  }

  const fieldSx = { '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '0.875rem' } }

  return (
    <Box>
      {/* Available toggle */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5, p: 1.5, borderRadius: '10px', bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
        <Box>
          <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#1e293b' }}>Available for delivery</Typography>
          <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8' }}>Rider can receive orders when active</Typography>
        </Box>
        <Switch
          checked={available}
          onChange={e => setAvailable(e.target.checked)}
          sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#22c55e' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#22c55e' } }}
        />
      </Box>

      {/* Name */}
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#374151', mb: 0.6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Full Name</Typography>
        <TextField
          fullWidth size="small"
          placeholder="Enter rider's full name"
          value={name}
          onChange={e => setName(e.target.value)}
          sx={fieldSx}
        />
      </Box>

      {/* Email / Username */}
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#374151', mb: 0.6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Email {!isEdit && <Typography component="span" sx={{ fontSize: '0.72rem', color: '#94a3b8', fontWeight: 400, textTransform: 'none' }}>(used as username)</Typography>}
        </Typography>
        <TextField
          fullWidth size="small" type="email"
          placeholder="Enter email address"
          value={email}
          onChange={e => setEmail(e.target.value)}
          disabled={isEdit}
          sx={fieldSx}
        />
      </Box>

      {/* Phone */}
      <Box sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#374151', mb: 0.6, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Phone Number</Typography>
        <TextField
          fullWidth size="small"
          placeholder="Enter phone number"
          value={phone}
          onChange={e => setPhone(e.target.value.replace(/[^0-9+\-\s]/g, ''))}
          sx={fieldSx}
        />
      </Box>

      {/* Info note (create only) */}
      {!isEdit && (
        <Box sx={{ mb: 2.5, p: 1.5, borderRadius: '10px', bgcolor: '#eff6ff', border: '1px solid #bfdbfe', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ fontSize: 16, color: '#3b82f6', flexShrink: 0 }}>ℹ</Box>
          <Typography sx={{ fontSize: '0.8rem', color: '#1e40af' }}>
            A temporary password will be emailed to the rider. They set their own delivery zone from their app.
          </Typography>
        </Box>
      )}

      {/* Password note (edit only) */}
      {isEdit && (
        <Box sx={{ mb: 2.5, p: 1.5, borderRadius: '10px', bgcolor: '#f0fdf4', border: '1px solid #bbf7d0', display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ fontSize: 16, color: '#16a34a', flexShrink: 0 }}>ℹ</Box>
          <Typography sx={{ fontSize: '0.8rem', color: '#166534' }}>
            Password can only be reset by the rider via the forgot password flow in their app.
          </Typography>
        </Box>
      )}

      {success && <Alert severity="success" sx={{ mb: 2, borderRadius: '10px', fontSize: '0.82rem' }}>{success}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '10px', fontSize: '0.82rem' }}>{error}</Alert>}

      {/* Buttons */}
      <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end' }}>
        <Button
          onClick={() => props.onClose?.()}
          sx={{ borderRadius: '10px', textTransform: 'none', color: '#64748b', fontWeight: 600, px: 2.5 }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <TwoWheelerIcon />}
          sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700, px: 3, bgcolor: '#0f172a', '&:hover': { bgcolor: '#1e293b' } }}>
          {loading ? 'Saving...' : isEdit ? 'Update Rider' : 'Add Rider'}
        </Button>
      </Box>
    </Box>
  )
}

export default withTranslation()(Rider)